import type { ProjectGrant, ProjectRole, ProjectTreeNode, TenantMemberSummary } from '@cms/contracts'

export const PROJECT_ROLES: readonly ProjectRole[] = [
  'cms.publisher',
  'cms.editor',
  'cms.author',
  'cms.reviewer',
  'cms.analyst',
  'cms.viewer',
]

export interface FlatProject {
  id: string
  name: string
  depth: number
}

interface MemberManagerClaims {
  roles: readonly string[]
  appRoles: readonly string[]
  scopes: readonly string[]
}

const MEMBER_MANAGER_ROLES = new Set(['owner', 'admin', 'cms.owner', 'cms.admin'])

/** Mirrors the server's visibility gate; it only hides UI and never authorizes a request. */
export function canManageTenantMembers(person: MemberManagerClaims | null): boolean {
  if (!person?.scopes.includes('cms:tenancy:write')) return false
  return [...person.roles, ...person.appRoles].some((role) => MEMBER_MANAGER_ROLES.has(role))
}

export function peopleViewLocked(dirty: boolean, busy: boolean): boolean {
  return dirty || busy
}

export function flattenProjectTree(nodes: readonly ProjectTreeNode[]): FlatProject[] {
  return nodes.flatMap((node) => [
    { id: node.id, name: node.name, depth: node.depth },
    ...flattenProjectTree(node.children),
  ])
}

export function rolesForProject(member: TenantMemberSummary, projectId: string): ProjectRole[] {
  return normalizeProjectRoles(member.projects.find((grant) => grant.projectId === projectId)?.roles ?? [])
}

export function normalizeProjectRoles(roles: readonly ProjectRole[]): ProjectRole[] {
  const selected = new Set(roles)
  return PROJECT_ROLES.filter((role) => selected.has(role))
}

export function projectRolesEqual(left: readonly ProjectRole[], right: readonly ProjectRole[]): boolean {
  const normalizedLeft = normalizeProjectRoles(left)
  const normalizedRight = normalizeProjectRoles(right)
  return normalizedLeft.length === normalizedRight.length && normalizedLeft.every((role, index) => role === normalizedRight[index])
}

/** Exact means exact: a parent grant never implies a child grant in the UI model. */
export function hasExactProjectGrant(grants: readonly ProjectGrant[], projectId: string): boolean {
  return grants.some((grant) => grant.projectId === projectId)
}
